import { create } from 'zustand';
import { generateClient } from 'aws-amplify/api';
import { listTechnicians } from '../../graphql/queries';
// Full-fidelity truck operations — the generated ones truncate nested
// estimate data (lines, payments), which corrupted write-backs.
import { listTrucksFull, onCreateTruckFull, onUpdateTruckFull, onDeleteTruckLite } from './truckGql';
import { DEFAULT_LOCATION_ID } from '../events/operationEvents';

const client = generateClient();

function belongsToActiveLocation(record) {
  return !record?.locationId || record.locationId === DEFAULT_LOCATION_ID;
}

function mergeById(...groups) {
  const map = new Map();
  groups.flat().filter(Boolean).forEach(item => map.set(item.id, item));
  return [...map.values()];
}

const SHOP_ID = 'DGP-ALBANY';

// ─── Shared domain layer ──────────────────────────────────────────────────────
export {
  // Legacy pipeline (backwards compat)
  PIPELINE,
  CLOSED_STATUSES,
  ACTIVE_STATUSES,
  IDLE_MS,
  STAGE_AGE_THRESHOLDS,
  STAGE_LABELS,
  NEXT_STAGES,
  // Simplified 5-stage pipeline
  STAGES,
  STATUS_TO_STAGE,
  // Activity stream
  ACTIVITY_TYPES,
  normalizeActivities,
  // Blocker model
  BLOCKER_TYPES,
  BLOCKER_LABELS,
  getActiveBlocker,
  isBlocked,
  getBlockerAge,
  // General helpers
  normalizeTechNumber,
  isClosedStatus,
  isActiveJob,
  getStageForStatus,
  getStageLabel,
  fmtDur,
  fmtAge,
  getPipelineIndex,
  getStageAge,
  getTimeHealth,
  getEstimatedHours,
  getJobElapsedMs,
  getJobHealth,
  isIdle,
  computeSlaRisk,
  SLA_RANK,
  computeShopHealth,
  getActiveJobs,
  groupJobsByStatus,
  groupJobsByPipelineStage,
  getJobsForTech,
} from '../../shared/domain';

import {
  PIPELINE,
  isIdle,
  computeShopHealth,
  getActiveJobs,
} from '../../shared/domain';

// ─── Store ───────────────────────────────────────────────────────────────────
const useOperationalStore = create((set, get) => ({
  // ── State ─────────────────────────────────────────────────────────────────
  trucks:      [],
  technicians: [],
  bays:        [],
  loading:     true,
  error:       null,
  shopId:      SHOP_ID,

  // Active view: 'flow' | 'ro-list' | 'kanban' | 'timeline' | 'shop-map' | 'command'
  activeView: 'flow',
  setActiveView: (view) => set({ activeView: view }),

  // View fullscreen (hides sidebar + header)
  isViewFullscreen: false,
  setViewFullscreen: (v) => set({ isViewFullscreen: v }),

  // Selected RO for detail panel
  selectedTruckId: null,
  setSelectedTruckId: (id) => set({ selectedTruckId: id }),
  getSelectedTruck: () => {
    const { trucks, selectedTruckId } = get();
    return trucks.find(t => t.id === selectedTruckId) ?? null;
  },

  // ── Derived selectors ─────────────────────────────────────────────────────
  getTrucksByStatus: (status) => get().trucks.filter(t => t.status === status),
  getTrucksByPipelineStage: (stageId) => {
    const stage = PIPELINE.find(s => s.id === stageId);
    if (!stage) return [];
    return get().trucks.filter(t => stage.statuses.includes(t.status));
  },
  getActiveTrucks: () => getActiveJobs(get().trucks),
  getIdleTrucks: () => get().trucks.filter(isIdle),
  getFleetDownTrucks: () => get().trucks.filter(t => t.isHardDown),

  getShopHealth: () => computeShopHealth(get().trucks),

  // ── Data loading ──────────────────────────────────────────────────────────
  fetchAll: async ({ quiet = false } = {}) => {
    if (!quiet) set({ loading: true, error: null });

    // Drain every page of a list query. DynamoDB scans return results in
    // arbitrary order with a page cap — without following nextToken, rows
    // beyond the first page silently never load (ROs "vanished" once the
    // table grew past one page).
    async function listAllPages(query, listKey, variables) {
      const items = [];
      let nextToken = null;
      let pages = 0;
      do {
        const res = await client.graphql({ query, variables: { ...variables, nextToken } });
        const conn = res.data[listKey];
        items.push(...(conn.items || []));
        nextToken = conn.nextToken;
        pages += 1;
      } while (nextToken && pages < 20);
      return items;
    }

    try {
      const locationFilter = { locationId: { eq: DEFAULT_LOCATION_ID } };
      const [truckItems, legacyTruckItems, techItems, legacyTechItems] = await Promise.all([
        listAllPages(listTrucksFull, 'listTrucks', { limit: 500, filter: locationFilter }),
        listAllPages(listTrucksFull, 'listTrucks', { limit: 500 }),
        listAllPages(listTechnicians, 'listTechnicians', { limit: 200, filter: locationFilter }),
        listAllPages(listTechnicians, 'listTechnicians', { limit: 200 }),
      ]);
      const trucks = mergeById(
        truckItems,
        legacyTruckItems.filter(belongsToActiveLocation)
      );
      const technicians = mergeById(
        techItems,
        legacyTechItems.filter(belongsToActiveLocation)
      );
      set(s => {
        const nowMs = Date.now();

        // Keep freshly-created trucks the eventually-consistent list read
        // hasn't caught up to yet — otherwise a poll right after createTruck
        // makes the new RO vanish from the board for a cycle.
        const fetchedIds = new Set(trucks.map(t => t.id));
        const pending = { ...s.pendingAdds };
        const keep = [];
        for (const [id, addedAt] of Object.entries(pending)) {
          if (fetchedIds.has(id) || nowMs - addedAt > 120_000) {
            delete pending[id]; // server caught up, or entry expired
          } else {
            const local = s.trucks.find(t => t.id === id);
            if (local) keep.push(local);
          }
        }

        // Same protection for recent optimistic UPDATES (drag between
        // columns, reorder, status change): a poll can return a stale copy
        // from just before the write and snap the card back. For a few
        // seconds after a local write, the local version outranks the fetch.
        const writes = { ...s.pendingWrites };
        for (const [id, wroteAt] of Object.entries(writes)) {
          if (nowMs - wroteAt > 15_000) delete writes[id];
        }
        const merged = trucks.map(t => {
          if (writes[t.id]) {
            const local = s.trucks.find(x => x.id === t.id);
            if (local) return local;
          }
          return t;
        });

        return {
          trucks: keep.length ? [...merged, ...keep] : merged,
          technicians,
          pendingAdds: pending,
          pendingWrites: writes,
          loading: false,
          error:   null,
        };
      });
    } catch (err) {
      console.error('[OperationalStore] fetchAll error:', err);
      if (!quiet) set({ loading: false, error: err });
    }
  },

  // ── Real-time subscriptions ───────────────────────────────────────────────
  _subs: [],
  startSubscriptions: () => {
    const subs = get()._subs;
    if (subs.length) return; // already running

    const upsert = (truck) => {
      if (!truck?.id) return;
      if (!belongsToActiveLocation(truck)) return;
      set(s => {
        const idx = s.trucks.findIndex(t => t.id === truck.id);
        if (idx === -1) return { trucks: [...s.trucks, truck] };
        const next = [...s.trucks];
        next[idx] = truck;
        return { trucks: next };
      });
    };

    const removeTruck = (id) => {
      if (!id) return;
      set(s => ({ trucks: s.trucks.filter(t => t.id !== id) }));
    };

    const onCreate = client.graphql({ query: onCreateTruckFull })
      .subscribe({ next: ({ data }) => upsert(data?.onCreateTruck) });

    const onUpdate = client.graphql({ query: onUpdateTruckFull })
      .subscribe({ next: ({ data }) => upsert(data?.onUpdateTruck) });

    const onDelete = client.graphql({ query: onDeleteTruckLite })
      .subscribe({ next: ({ data }) => removeTruck(data?.onDeleteTruck?.id) });

    set({ _subs: [onCreate, onUpdate, onDelete] });
  },

  stopSubscriptions: () => {
    get()._subs.forEach(s => s.unsubscribe?.());
    set({ _subs: [] });
  },

  // ── Local optimistic updates (before the subscription echo comes back) ────
  // pendingWrites shields each updated truck from stale poll data for a few
  // seconds (see fetchAll).
  pendingWrites: {},
  localUpdateTruck: (id, patch) =>
    set(s => ({
      trucks: s.trucks.map(t => t.id === id ? { ...t, ...patch } : t),
      pendingWrites: { ...s.pendingWrites, [id]: Date.now() },
    })),

  // Insert a just-created truck immediately. AppSync list queries are
  // eventually consistent — a refetch right after createTruck can miss the
  // new row, which made fresh ROs "not appear" until the next poll. The
  // pendingAdds entry protects it from being clobbered by fetchAll until
  // the server list includes it (expires after 2 minutes).
  pendingAdds: {},
  localAddTruck: (truck) =>
    set(s => ({
      trucks: s.trucks.some(t => t.id === truck.id) ? s.trucks : [...s.trucks, truck],
      pendingAdds: { ...s.pendingAdds, [truck.id]: Date.now() },
    })),
}));

export default useOperationalStore;
